import { useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AudioService } from '../../../services/text-to-audio/texttoaudio.service';
import { extractSpeechText } from '../../../utils/extractSpeechText';
import { isAppAudioEnabled } from '../../../utils/audioManager';
import { isTTSAudioEnabled } from '../../../configs/config';
import { useAudioStore } from '../../../utils/audioStore';
import { Interaction } from '../types/lesson.types';

interface UseLessonAudioOptions {
  isResuming: boolean;
  lessonId?: string;
}

export const useLessonAudio = ({ isResuming, lessonId }: UseLessonAudioOptions) => {
  const { t } = useTranslation();
  const audioServiceRef = useRef<AudioService>(new AudioService());
  const isPlayingRef = useRef(false);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      audioServiceRef.current.stopAudio();
    };
  }, []);

  /**
   * Called when a step's typing animation completes.
   * Extracts text, sends to server for LaTeX→Hebrew conversion + TTS, plays result.
   */
  const playForInteraction = useCallback(async (
    interaction: Interaction,
    isCurrent: boolean
  ) => {
    // Guards: only play for current step, not on resume, and audio enabled
    if (!isCurrent || isResuming || !isAppAudioEnabled() || !isTTSAudioEnabled()) return;
    if (interaction.type !== 'content' && interaction.type !== 'quiz') return;

    try {
      isPlayingRef.current = true;
      const text = extractSpeechText(interaction, t);
      if (!text || text.trim().length === 0) return;

      await audioServiceRef.current.handleLessonTextToAudio(text, interaction.id, lessonId);
    } catch (error) {
      console.error('useLessonAudio: playback failed', error);
    } finally {
      isPlayingRef.current = false;
    }
  }, [isResuming, t, lessonId]);

  /**
   * Pre-fetches audio for an interaction in the background and caches it in useAudioStore.
   * Does NOT play audio.
   */
  const prefetchInteraction = useCallback(async (interaction: Interaction | undefined) => {
    if (!interaction) return;
    if (!isAppAudioEnabled() || !isTTSAudioEnabled()) return;
    if (interaction.type !== 'content' && interaction.type !== 'quiz') return;

    try {
      const audioStore = useAudioStore.getState();
      const cacheKey = lessonId ? `${lessonId}_${interaction.id}` : interaction.id;
      
      // Skip if already cached
      if (audioStore.getAudioByKey?.(cacheKey)) return;

      const text = extractSpeechText(interaction, t);
      if (!text || text.trim().length === 0) return;

      console.log(`[useLessonAudio] Pre-fetching background audio for interaction ${interaction.id} (key: ${cacheKey})...`);
      const audioSources = await audioServiceRef.current.fetchLessonAudio(text, interaction.id, lessonId);
      if (audioSources && audioSources.length > 0) {
        audioStore.setAudioByKey?.(cacheKey, JSON.stringify(audioSources));
        console.log(`[useLessonAudio] Successfully pre-fetched & cached audio for interaction ${interaction.id}`);
      }
    } catch (error) {
      console.warn(`useLessonAudio: pre-fetch failed for interaction ${interaction?.id}`, error);
    }
  }, [t, lessonId]);

  /**
   * Replay audio for a specific interaction (triggered by replay button).
   */
  const replayForInteraction = useCallback(async (interaction: Interaction) => {
    if (!isAppAudioEnabled() || !isTTSAudioEnabled()) return;
    if (interaction.type !== 'content' && interaction.type !== 'quiz') return;

    try {
      isPlayingRef.current = true;
      const text = extractSpeechText(interaction, t);
      if (!text || text.trim().length === 0) return;

      await audioServiceRef.current.handleLessonTextToAudio(text, interaction.id, lessonId);
    } catch (error) {
      console.error('useLessonAudio: replay failed', error);
    } finally {
      isPlayingRef.current = false;
    }
  }, [t, lessonId]);

  /**
   * Toggles pause/resume for a specific interaction.
   * If audio is playing -> pause.
   * If audio is paused -> resume from exact timestamp.
   * If audio is inactive -> start playing from beginning.
   */
  const togglePauseForInteraction = useCallback(async (interaction: Interaction) => {
    if (!isTTSAudioEnabled()) return;
    if (!isAppAudioEnabled()) return;

    console.log('[togglePause] called for:', interaction.id, 
                'isPausedForInteraction:', AudioService.isPausedForInteraction(interaction.id),
                'isPlaying:', AudioService.isPlaying(),
                'currentAudio:', !!AudioService.getCurrentAudio());

    // 1. If audio is paused for this interaction, resume it
    if (AudioService.isPausedForInteraction(interaction.id)) {
      console.log('[togglePause] -> resumeAudio');
      AudioService.resumeAudio();
      return;
    }

    // 2. If audio is actively playing, pause it
    if (AudioService.isPlaying()) {
      console.log('[togglePause] -> pauseAudio');
      AudioService.pauseAudio();
      return;
    }

    // 3. No active audio — start fresh playback
    console.log('[togglePause] -> replayForInteraction');
    await replayForInteraction(interaction);
  }, [lessonId, replayForInteraction]);

  /**
   * Stop any currently playing lesson audio.
   */
  const stopAudio = useCallback(() => {
    audioServiceRef.current.stopAudio();
    isPlayingRef.current = false;
  }, []);

  return {
    playForInteraction,
    prefetchInteraction,
    replayForInteraction,
    togglePauseForInteraction,
    stopAudio,
    isPlaying: isPlayingRef.current
  };
};
